import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { featureFlagsAPI } from '../services/api';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { ArrowLeft, Flask, Globe, EyeSlash, CheckCircle } from '@phosphor-icons/react';

const PHASE_META = {
  1: { title: 'Phase 1 — Core GTM Engine', color: '#22C55E', desc: 'Profiles, marketplace, wallet, send/receive money, DiviSpend micro-investing, payment rails — plus fraud detection and GDPR tools built in from day one.' },
  2: { title: 'Phase 2 — Trust & Retention', color: '#3B82F6', desc: 'SMS 2FA, CAPTCHA, email receipts, PDF invoices, and the in-app notification center.' },
  5: { title: 'Phase 5 — Post-GTM Backlog', color: '#64748B', desc: 'Pro tier, themes, CRM, widgets, analytics — deliberately parked until the core product proves demand.' },
};

const PHASES = [1, 2, 5];

export default function RoadmapPage() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const { isAdmin, isBeta } = useFeatureFlags();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await featureFlagsAPI.getRoadmap();
        setFlags(data.flags || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Roadmap is restricted to beta users and admins.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const map = { 1: [], 2: [], 5: [] };
    flags.forEach(f => { if (map[f.phase]) map[f.phase].push(f); });
    return map;
  }, [flags]);

  return (
    <div className="min-h-screen bg-[#111111] text-white" data-testid="roadmap-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/dashboard" className="text-[#999999] hover:text-[#22C55E] flex items-center gap-2 text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#22C55E]">Public Roadmap</span>
          {isAdmin && <span className="text-[10px] uppercase bg-[#22C55E]/20 text-[#22C55E] px-2 py-0.5 rounded font-bold">Admin View</span>}
          {!isAdmin && isBeta && <span className="text-[10px] uppercase bg-[#3B82F6]/20 text-[#3B82F6] px-2 py-0.5 rounded font-bold">Beta View</span>}
        </div>
        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Cabinet Grotesk' }}>
          What we're building, in phases
        </h1>
        <p className="text-[#999] max-w-2xl mb-10">
          Live track of every feature shipping into Divispend. Status updates in real time as the team flips switches.
        </p>

        {loading ? (
          <p className="text-[#999]">Loading…</p>
        ) : err ? (
          <div className="border border-[#222] bg-[#0d0d0d] rounded-xl p-8 text-center">
            <Flask className="w-10 h-10 text-[#3B82F6] mx-auto mb-3" />
            <p className="text-[#999]">{err}</p>
            <p className="text-[10px] text-[#666] mt-2">Have an invite code? Redeem it from your profile to unlock the roadmap.</p>
          </div>
        ) : (
          <div className="space-y-12">
            {PHASES.map(phase => {
              const items = grouped[phase] || [];
              const meta = PHASE_META[phase];
              const liveCount = items.filter(i => i.enabled && !i.beta_only).length;
              const betaCount = items.filter(i => i.enabled && i.beta_only).length;
              return (
                <section key={phase} className="border border-[#222] rounded-xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-[#222] bg-[#0a0a0a] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-full" style={{ background: meta.color }} />
                      <h2 className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk' }}>{meta.title}</h2>
                    </div>
                    <div className="text-[10px] text-[#666]">{liveCount} live · {betaCount} beta · {items.length} total</div>
                  </div>
                  <p className="px-6 pt-4 text-sm text-[#999]">{meta.desc}</p>

                  <div className="px-6 py-5 grid sm:grid-cols-2 gap-3">
                    {items.length === 0 ? (
                      <p className="text-sm text-[#555] col-span-2">No features in this phase yet.</p>
                    ) : items.map(f => {
                      const status = !f.enabled
                        ? { label: 'Planned', icon: <EyeSlash className="w-3.5 h-3.5" />, color: '#666' }
                        : f.beta_only
                          ? { label: 'In Beta', icon: <Flask className="w-3.5 h-3.5" />, color: '#3B82F6' }
                          : { label: 'Live', icon: <Globe className="w-3.5 h-3.5" />, color: '#22C55E' };
                      return (
                        <div key={f.key} className="bg-[#0d0d0d] border border-[#1f1f1f] rounded-lg p-4 hover:border-[#22C55E]/30 transition-colors" data-testid={`roadmap-item-${f.key}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <h4 className="text-sm font-semibold">{f.name}</h4>
                            <span
                              className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold"
                              style={{ background: `${status.color}20`, color: status.color }}
                            >
                              {status.icon}{status.label}
                            </span>
                          </div>
                          <p className="text-xs text-[#999] leading-relaxed">{f.description}</p>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
